import { Box } from '@chakra-ui/react';
import _ from 'lodash';
import React, { FC } from 'react';

import { AudioConfigTooltipContentfulPaths } from 'Containers/ProfilePage/ProfileAudio';
import { ProfileAudio } from 'ContentfulDefaults/types/profile';
import { useTextContext } from 'Contexts/textContext';
import { AudioTooltip } from 'DesignLibrary/atoms/AudioTooltip';

import { InfoBarStyle, InfoContent } from './styled';
import { Button, ButtonTarget, Tooltip } from '../../atoms';
import { Icon, IconTypes } from '../../atoms/Icon';
import { Paragraph } from '../../atoms/typography';
import { PrimaryColors } from '../../vars';

export interface InfoBarButton {
  text?: string;
  url?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  target?: ButtonTarget;
}

// There is a chance that HTMLAttributes might not be the right interface
// see: https://github.com/typescript-cheatsheets/react/issues/128
// But it satisfies styled components and seems to define the desired attributes
export interface InfoBarProps extends React.HTMLAttributes<HTMLDivElement> {
  text?: JSX.Element | string;
  header?: string | React.ReactNode;
  stretch?: boolean;
  showAccent?: boolean;
  icon?: IconTypes;
  iconOverride?: JSX.Element;
  buttons?: InfoBarButton[];
  color?: PrimaryColors;
  colorText?: boolean;
  inline?: boolean;
  size?: 'sm' | 'md' | 'lg';
  showClaimsTooltip?: boolean;
  isProfileAudioEnabled?: boolean;
  claimsTooltipMessage?: string;
  claimsErrorState?: string | null;
}

const InfoBar: FC<InfoBarProps> = ({
  header,
  text = 'Fix Me',
  stretch = true,
  showAccent = true,
  icon = null,
  iconOverride,
  buttons = [],
  color = 'blue',
  colorText = false,
  inline = true,
  size = 'md',
  children,
  showClaimsTooltip,
  claimsTooltipMessage,
  isProfileAudioEnabled = false,
  claimsErrorState,
  ...rest
}): JSX.Element => {
  const { retrieveContentfulData } = useTextContext();
  const audioConfig = retrieveContentfulData<ProfileAudio>(
    AudioConfigTooltipContentfulPaths.ClaimsFeedbackHouseholdAndRiskSection,
    {} as ProfileAudio,
  );
  const tooltipId = `tooltip-${_.uniqueId()}`;
  const audioTooltipId = `audio-${tooltipId}`;
  return (
    <InfoBarStyle color={color} colorText={colorText} stretch={stretch} showAccent={showAccent} {...rest}>
      {isProfileAudioEnabled && (
        <Box pos="absolute" top={4} left={['initial', -5]} right={[4, 'initial']}>
          <AudioTooltip
            data-testid={audioTooltipId}
            urlToAudio={audioConfig.audio_file_url}
            urlToClosedCaptionVtt={audioConfig.vtt_transcript_file_url}
          />
        </Box>
      )}

      <InfoContent size={size}>
        <div className="align-icon">
          {!iconOverride && icon && (
            <div data-testid="icon">
              <Icon type={icon} color={`--primary-${color}`} weight="fill" />
            </div>
          )}
          {iconOverride && iconOverride}
          <Paragraph color={colorText ? `--colors-${color}-600` : '--text-black'}>
            {header && <b>{header} &nbsp;</b>}
            {inline ? text : <span style={{ display: 'block' }}>{text}</span>}
            {showClaimsTooltip && (
              <Tooltip id="claims-data-tooltip" size="normal" label={claimsTooltipMessage} />
            )}
          </Paragraph>
        </div>
        {claimsErrorState !== 'provider_gone' &&
          claimsErrorState !== 'rate_limited' &&
          buttons.length > 0 && (
            <div className="button-container">
              {buttons.map(({ text, url, onClick, target }, idx) => {
                if (url) {
                  return (
                    <Button buttonType="transparent" key={idx} size="small" url={url} target={target}>
                      {text}
                    </Button>
                  );
                }
                if (onClick) {
                  return (
                    <Button key={idx} size="small" onClick={onClick}>
                      {text}
                    </Button>
                  );
                }
                return null;
              })}
            </div>
          )}
        {(claimsErrorState === 'provider_gone' || claimsErrorState === 'rate_limited') &&
          buttons?.length > 0 && (
            <div className="button-container">
              {buttons[0]?.onClick && (
                <Button size="small" onClick={buttons[0].onClick}>
                  {claimsErrorState === 'provider_gone' ? 'Okay' : 'Got it'}
                </Button>
              )}
            </div>
          )}
        {children}
      </InfoContent>
    </InfoBarStyle>
  );
};

export { InfoBarStyle };
export default InfoBar;
